import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { videoTo911Api } from './videoTo911Api';

const initialState = {
  rooms: {},
};

// Returns a copy of `rooms` with `roomId` removed and every other room kept.
const removeRoom = (rooms, roomId) =>
  Object.keys(rooms)
    .filter(key => key !== roomId)
    .reduce((obj, key) => {
      obj[key] = rooms[key];
      return obj;
    }, {});

export const videoTo911Slice = createSlice({
  name: 'videoTo911',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addMatcher(
        videoTo911Api.endpoints.createRoom.matchFulfilled,
        (state, action) => {
          const callerId = action.meta.arg.originalArgs?.callerId;
          const callStartTime = action.meta.arg.originalArgs?.callStartTime;
          return {
            ...state,
            rooms: {
              ...state.rooms,
              [action.payload.room_id]: {
                ...action.payload,
                callerId,
                callStartTime, // used for ending the room
              },
            },
          };
        },
      )
      .addMatcher(
        videoTo911Api.endpoints.refreshClientToken.matchPending,
        (state, action) => {
          const roomId = action.meta.arg.originalArgs?.roomId;
          return {
            ...state,
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...state.rooms?.[roomId],
              },
            },
          };
        },
      )
      .addMatcher(
        videoTo911Api.endpoints.refreshClientToken.matchFulfilled,
        (state, action) => {
          const roomId = action.meta.arg.originalArgs?.roomId;
          return {
            ...state,
            rooms: {
              ...state.rooms,
              [roomId]: {
                ...state.rooms?.[roomId],
                ...action.payload,
              },
            },
          };
        },
      )
      .addMatcher(
        videoTo911Api.endpoints.refreshClientToken.matchRejected,
        (state, action) => {
          // Only a 400 means the server rejected the refresh token/room as
          // invalid. Any other failure (5xx, network error, no status) is
          // transient and must not tear down an active video session.
          if (action.payload?.status !== 400) {
            return state;
          }
          const roomId = action.meta.arg.originalArgs?.roomId;
          return {
            ...state,
            rooms: removeRoom(state.rooms, roomId),
          };
        },
      )
      .addMatcher(
        isAnyOf(
          videoTo911Api.endpoints.endRoom.matchFulfilled,
          videoTo911Api.endpoints.endRoom.matchRejected,
        ),
        (state, action) => {
          // Regardless of API success/failure, the UI will appear as succesful
          const roomId = action.meta.arg.originalArgs?.roomId;
          return {
            ...state,
            rooms: removeRoom(state.rooms, roomId),
          };
        },
      )
      .addMatcher(
        action => action.type === 'emergencyCalls/removeCall',
        (state, action) => {
          // Remove any rooms for the given caller ID
          Object.keys(state.rooms).forEach(roomID => {
            if (state.rooms[roomID].callerId === action.payload) {
              delete state.rooms[roomID];
            }
          });
        },
      )
      .addMatcher(
        action => action.type === 'emergencyCalls/removeAllCalls',
        () => {
          return initialState;
        },
      )
      .addMatcher(
        action => ['LOGOUT_SUCCESS', 'SINATRA_LOGOUT'].includes(action.type),
        () => {
          return initialState;
        },
      );
    // .addCase(removeFromQueue)
  },
});

const persistConfig = {
  key: 'videoTo911',
  storage,
};

const persistedReducer = persistReducer(persistConfig, videoTo911Slice.reducer);

export default persistedReducer;
